import moment from "moment";
import { AlertTriangle } from "lucide-react";

// PO-REJECTION-RESTRUCTURE ==================================================
// Mirrors src/pages/invoice-homepage/RejectionDetailsCard.tsx, adapted for the
// PO domain's simpler rejection_data_desc shape (rejection_remark/remarks/
// rejected_by_name/rejected_at — no rejection_header/sub_remarks/mandatory_inputs).

const formatRejectionCode = (code?: string) =>
  code ? code.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

const formatKey = (key: string) =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const isIsoDateString = (value: any) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value) && !isNaN(new Date(value).getTime());

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  if (isIsoDateString(value)) {
    const d = new Date(value);
    return `${d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })}, ${d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  }
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const labelOverrides: Record<string, string> = {
  rejection_remark: "Rejection Remarks",
  remarks: "Additional Remarks",
  rejected_by_name: "Rejected By",
  rejected_at: "Rejected Date",
};
const orderedKeys = ["rejection_remark", "remarks", "rejected_by_name", "rejected_at"];
// rejected_by/rejected_by_role are internal-only, same exclusion invoice's card applies.
const excludedKeys = new Set(["rejected_by", "rejected_by_role"]);

interface PoRejectionDetailsCardProps {
  data: any;
}

const PoRejectionDetailsCard = ({ data }: PoRejectionDetailsCardProps) => {
  if (!data?.rejection_code) return null;

  const combinedData: Record<string, any> = { ...(data?.rejection_data_desc || {}) };
  const extraKeys = Object.keys(combinedData).filter(
    (key) => !orderedKeys.includes(key) && !excludedKeys.has(key)
  );

  const entries = [...orderedKeys, ...extraKeys]
    .map((key): [string, any] => [key, combinedData[key]])
    .filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <div className="mx-5 mb-5 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] overflow-hidden">
      <div className="px-5 py-3 bg-[#FEE2E2] border-b border-[#FCA5A5] flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-[#7F1D1D]" aria-hidden="true" />
        <h3 className="text-[#7F1D1D] font-semibold text-base">Rejection Details</h3>
      </div>

      <div className="px-5 py-4 space-y-4">
        <span className="inline-block text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full bg-[#FEE2E2] text-[#9B2C2C] border border-[#FCA5A5]">
          {formatRejectionCode(data?.rejection_code)}
        </span>

        {entries.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm pt-2 border-t border-[#FCA5A5]/40">
            {entries.map(([key, value]) => {
              const formatted = formatValue(value);
              const isLong = formatted.length > 60 || /\n/.test(formatted);
              return (
                <div key={key} className={`flex flex-col ${isLong ? "sm:col-span-2" : ""}`}>
                  <p className="text-xs uppercase tracking-wide text-[#9B2C2C] font-semibold mb-1">
                    {labelOverrides[key] || formatKey(key)}
                  </p>
                  <span className="text-[#303030] font-medium leading-relaxed whitespace-pre-wrap break-words">
                    {formatted}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {(data?.rejection_review_action_by || data?.rejection_review_action_date) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs pt-3 border-t border-dashed border-[#FCA5A5]/40 text-[#8A8A8A]">
            <div className="flex items-start gap-2">
              <p className="min-w-[110px]">Reviewed By</p>
              <span>{data?.rejection_review_action_by || "—"}</span>
            </div>
            <div className="flex items-start gap-2">
              <p className="min-w-[110px]">Reviewed On</p>
              <span>
                {data?.rejection_review_action_date
                  ? moment(data.rejection_review_action_date).format("MMMM Do YYYY, hh:mm A")
                  : "—"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PoRejectionDetailsCard;
// PO-REJECTION-RESTRUCTURE ==================================================
